import { useEffect, useRef, useState, useCallback } from "react";
import { refreshSession } from "@/services/auth.service";

const WARNING_BEFORE_MS = 2 * 60 * 1000;
const IDLE_THRESHOLD_MS = 5 * 60 * 1000;
const ACTIVITY_EVENTS = ["mousemove", "keydown", "click", "scroll", "touchstart"] as const;

export interface SessionWarningState {
  show: boolean;
  secondsLeft: number;
}

export function useSessionWarning(
  sessionExpiresAt: number | null | undefined,
  onSilentRefresh?: () => Promise<void>
) {
  const [state, setState] = useState<SessionWarningState>({ show: false, secondsLeft: 0 });
  const warningTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const countdownRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const lastActivityAt = useRef<number>(Date.now());

  const clearTimers = useCallback(() => {
    if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
    if (countdownRef.current) clearInterval(countdownRef.current);
    warningTimerRef.current = null;
    countdownRef.current = null;
  }, []);

  useEffect(() => {
    const updateActivity = () => { lastActivityAt.current = Date.now(); };
    ACTIVITY_EVENTS.forEach(e => document.addEventListener(e, updateActivity, { passive: true }));
    return () => ACTIVITY_EVENTS.forEach(e => document.removeEventListener(e, updateActivity));
  }, []);

  const startCountdown = useCallback((expiresAt: number) => {
    setState({ show: true, secondsLeft: Math.max(0, Math.round((expiresAt - Date.now()) / 1000)) });
    countdownRef.current = setInterval(() => {
      const secs = Math.max(0, Math.round((expiresAt - Date.now()) / 1000));
      setState((prev) => ({ ...prev, secondsLeft: secs }));
      if (secs === 0) {
        clearTimers();
        window.location.href = "/auth/expired";
      }
    }, 1000);
  }, [clearTimers]);

  const scheduleWarning = useCallback(
    (expiresAt: number) => {
      clearTimers();
      const now = Date.now();
      const msUntilWarning = expiresAt - now - WARNING_BEFORE_MS;

      const fire = async () => {
        const isIdle = Date.now() - lastActivityAt.current >= IDLE_THRESHOLD_MS;
        if (!isIdle && onSilentRefresh) {
          await onSilentRefresh();
          // onSilentRefresh updates sessionExpiresAt in the parent, which re-triggers scheduleWarning
          return;
        }
        startCountdown(expiresAt);
      };

      if (msUntilWarning <= 0) {
        fire();
        return;
      }
      warningTimerRef.current = setTimeout(fire, msUntilWarning);
    },
    [clearTimers, startCountdown, onSilentRefresh]
  );

  useEffect(() => {
    if (sessionExpiresAt) {
      scheduleWarning(sessionExpiresAt);
    }
    return clearTimers;
  }, [sessionExpiresAt, scheduleWarning, clearTimers]);

  const handleStayLoggedIn = useCallback(async () => {
    const ok = await refreshSession();
    if (ok) {
      clearTimers();
      setState({ show: false, secondsLeft: 0 });
    }
  }, [clearTimers]);

  const handleDismiss = useCallback(() => {
    clearTimers();
    setState({ show: false, secondsLeft: 0 });
  }, [clearTimers]);

  return { ...state, handleStayLoggedIn, handleDismiss };
}
